import { ref, reactive, computed, watchEffect, watch } from 'vue'
import { defineStore } from 'pinia';
import { useSessionStore } from '@/stores/SessionStore'
import { useUpdateKey } from '@/composables/useUpdateKey'
import { useApiProfile } from '@/composables/useApiProfile'
import useStorageSystem from '@/composables/useStorageSystem';
import useConfigurationGlobals from "@/composables/useConfigurationGlobals";
import useUserGlobals from '@/composables/useUserGlobals'
import useSettingsGlobals from '@/composables/useSettingsGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { $WEB_VIEW } = useSettingsGlobals()   

const { 
  $ACCESS_LEVEL, 
  $CAN_ADS_TOGGLE, 
  $DEFAULT_ADS_TOGGLE_STATE, 
  $MEMBER_LEVEL, 
  $MEMBER_LEVEL_NAME, 
  $NON_TRIAL_MEMBER_LEVEL, 
  $NON_TRIAL_MEMBER_LEVEL_NAME, 
  $SHOW_ADS_TOGGLE, 
  $WF_USERNAME, 
  $MESSAGES, 
  $FIRST_TIME_USER, 
  $USER_SIGNED_IN,
  $SETTINGS
} = useUserGlobals();

export let useUserStore = defineStore('userStore', () => {
  // TODO: Move profiles and profile data into separate store
    const sessionStore = useSessionStore();
    const apiProfile = useApiProfile()

    const updateKey = useUpdateKey();

    const initialized = ref(false);
    const accessLevel = ref($ACCESS_LEVEL);
    const canAdsToggle = ref($CAN_ADS_TOGGLE)
    const defaultAdsToggleState = ref($DEFAULT_ADS_TOGGLE_STATE)
    const memberLevel = ref($MEMBER_LEVEL)
    const memberLevelName = ref($MEMBER_LEVEL_NAME)
    const nonTrialMemberLevel = ref($NON_TRIAL_MEMBER_LEVEL)
    const nonTrialMemberLevelName = ref($NON_TRIAL_MEMBER_LEVEL_NAME)
    const showAdsToggle = ref($SHOW_ADS_TOGGLE)
    const userName = ref($WF_USERNAME)
    const userProfiles = ref(null);
    const currentProfile = ref(0);
    const userSignedIn = ref($USER_SIGNED_IN ? $USER_SIGNED_IN.toLowerCase() !== 'false' : true)
    const firstTimeUser = ref($FIRST_TIME_USER);
    const messages = ref(null);
    
    const apiStorage = ref(null);
    const loadingAPIStorage = ref(false);
    
    const favoriteSpotData = ref(null);
    const favoriteSpotContainers = ref({})

    const showSettingsModal = ref(false);
    
    const [lastSelectedProfileId] = useStorageSystem(null, 'lastSelectedProfileId', 'localStorage');
    const [favoriteSortOrder] = useStorageSystem('windspeed', 'FavSortOrder', 'localStorage');
    const [unitsWind] = useStorageSystem(null, 'SpeedUnits', 'localStorage');
    const [unitsTemp] = useStorageSystem(null, 'TemperatureUnits', 'localStorage');
    const [unitsDistance] = useStorageSystem(null, 'DistanceUnits', 'localStorage')
    const [unitsPrecip] = useStorageSystem(null, 'PrecipUnits', 'localStorage');
    const [unitsHeight] = useStorageSystem(null, 'HeightUnits', 'localStorage');
    const [showNCWindToggle] = useStorageSystem(null, 'ShowNCWindToggle', 'localStorage');
    const [showAds] = useStorageSystem(true, 'ShowAds', 'localStorage');
    const [searchResultsDisplay] = useStorageSystem('windlist', 'SearchResultsDisplay', 'localStorage')

    const currentProfileId = computed(() => currentProfile.value?.profile_id);
    const profileContainers = computed(() => currentProfile.value?.containers.filter(container => container.container_type_id.toLowerCase() !== 'map'));
    const profileMaps = computed(() => currentProfile.value?.containers.filter(container => container.container_type_id.toLowerCase() === 'map'));
    const profileAllContainers = computed(() => currentProfile.value?.containers);

    const units = reactive({
      wind: unitsWind,
      temp: unitsTemp,
      precip: unitsPrecip,
      distance: unitsDistance,
      height: unitsHeight,
      short_distance: unitsDistance.value === 'mi' ? 'ft' : 'm'
    })

    try {
      messages.value = [];

      if ($MESSAGES && $MESSAGES !== '') {
        messages.value = JSON.parse($MESSAGES);
      }
    } catch(error) {
      console.warn('Could not parse user messages: ', error);
    }

    // TODO: Move fetch into composable
    async function getProfiles(refresh = false) {
      if (userProfiles.value != null && !refresh) return userProfiles.value;

      try {
        const response = await fetch(
          `${$BASEURL}/profile/getProfiles?wf_token=${sessionStore.token}&units_wind=mph&units_temp=f&units_distance=mi&units_precip=in`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json();

        if (data.status.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        data.profiles.forEach(profile => {
          favoriteSpotContainers.value[profile.profile_id] = profile.containers.shift();
        })

        data.profiles = changeMasterProfileNameToMain(data.profiles)
        userProfiles.value = data;

        if (!currentProfileId.value && !lastSelectedProfileId.value) {
          setDefaultProfile();
        } else if (lastSelectedProfileId.value) {
          setCurrentProfile(lastSelectedProfileId.value);
        } else {
          setCurrentProfile(currentProfileId.value)
        }

        return userProfiles.value
      } catch (error) {
        console.warn(error)
      }           
    }

    async function sortProfiles(profileIdList) {
      const params = {
        profile_id_list: profileIdList.join(','),
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/sortProfiles?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function addProfile(name) {
      const params = {
        name,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/addProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function deleteProfile(profileId) {
      const params = {
        profile_id: profileId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/deleteProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function copyProfile(profileId, name) {
      const params = {
        name,
        profile_id: profileId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/copyProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function updateProfile(profileId, name) {
      const params = {
        name,
        profile_id: profileId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/updateProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function addSpotToProfile(profileId, spotId, { name, lat, lon }) {
      const params = {
        profile_id: profileId,
        spot_id: spotId,
        name: name,
        lat: !spotId ? lat : null,
        lon: !spotId ? lon : null,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/addSpotToProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function removeSpotFromProfile(profileId, favSpotId, refreshSpotData = false) {
      try {
        const response = await fetch( `${$BASEURL}/profile/removeSpotFromProfile?profile_id=${profileId}&fav_spot_id=${favSpotId}&wf_token=${sessionStore.token}` )

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json();

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        if (refreshSpotData) {
          getSpotDetailSetByProfile();
        }

        return data;
      } catch (error) {
        console.warn(error);
      }  
    }

    async function sortSpotsInProfile(profileId, favSpotIdList) {
      const params = {
        profile_id: profileId,
        fav_spot_id_list: favSpotIdList.join(','),
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/sortSpotsInProfile?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }      
    }

    async function updateProfileContainer(containerId, name, settings) {

      const params = {
        name,
        container_id: containerId,
        options: settings ? JSON.stringify(settings) : null,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/updateProfileContainer?${queryString}`
        )

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }        

        return data;
      } catch (error) {
        console.warn(error)
      }
    }

    async function sortProfileContainers(profileId, containerIdList) {

      const params = {
        profile_id: profileId,
        container_id_list: containerIdList.join(','),
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(`${$BASEURL}/profile/setProfileContainerOrder?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }        

        return data;
      } catch (error) {
        console.warn(error)
      }
    }

    async function addContainerToProfile(profileId, containerTypeId, name, settings) {

      const params = {
        profile_id: profileId,
        container_type_id: containerTypeId,
        options: typeof settings === 'string' ? settings : JSON.stringify(settings),
        name: name,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/addContainerToProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        return data;
      } catch (error) {
        console.warn(error)
      }

    }

    async function removeContainerFromProfile(containerId) {

      const params = {
        container_id: containerId,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/profile/removeContainerFromProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        return data;
      } catch (error) {
        console.warn(error)
      }

    }

    async function deleteSpotsFromProfile(favSpotIds, profileId) {
      if (!favSpotIds) {
        console.warn(
          'A favorite spot id or array of favorite spot ids are required to delete favorite spots from profile'
        )
        return
      }

      if (!Array.isArray(favSpotIds) && typeof favSpotIds !== 'number') {
        console.warn(
          'Favorite spot ids prop expects an array of favorite spot ids or a single favorite spot id'
        )
        return
      }

      const favSpotIdArray = Array.isArray(favSpotIds) ? favSpotIds : [favSpotIds]
      const requests = favSpotIdArray.map((favSpotId) =>
        removeSpotFromProfile(profileId, favSpotId)
      )

      await Promise.all(requests)
    }

    async function deleteContainers(containerIds) {
      if (!containerIds) {
        console.warn('A container id or array of containers ids are required to delete containers');
        return;
      }

      if (!Array.isArray(containerIds) && typeof containerIds !== 'number') {
        console.warn('Container ids prop expects an array of container ids or a single container id');
        return;
      }

      const containerIdArray = Array.isArray(containerIds) ? containerIds : [containerIds];
      const requests = containerIdArray.map((containerId) => removeContainerFromProfile(containerId));

      await Promise.all(requests);
    }

    async function moveCopyItemsToProfiles({items, profiles, type, action}) {
      if (!items || !profiles || !action) {
        console.warn('Items, profiles, and action are required to move or copy containers to profiles');
        return;
      }

      if (!Array.isArray(items) && typeof items !== 'object') {
        console.warn('Items prop expects an item object or an array of item objects');
        return;
      }

      const itemArray = Array.isArray(items) ? items : [items];
      let requests = [];

      itemArray.forEach((item) => {
        if (type === 'container') {
          requests = requests.concat(getSaveContainerToProfilesRequests(item, profiles, action))
        }
        else if (type === 'spot') {
          requests = requests.concat(getSaveSpotToProfilesRequests(item, profiles, action))
        }
      })
      
      return await Promise.all(requests);
    }

    function getSaveSpotToProfilesRequests(spot, profiles, action) {
      return profiles.map(async (profileId) => {
        const result = await addSpotToProfile(profileId, spot.spot_id, {
          name: spot.name,
          lat: spot.lat,
          lon: spot.lon
        })
        if (!result) return
        if (action === 'move') {
          console.log('move')
          await removeSpotFromProfile(currentProfileId.value, spot.fav_spot_id)
        }
      })
    } 

    function getSaveContainerToProfilesRequests(container, profiles, action) {
      return profiles.map(async (profileId) => {
        const result = await addContainerToProfile(
          profileId,
          container.container_type_id,
          container.container_name,
          container.options
        )
        if (!result) return
        if (action === 'move') {
          await removeContainerFromProfile(container.container_id)
        }
      })
    } 

    function getFavoriteSpotById(spotId) {
      return favoriteSpotData.value?.spots.find(spot => spot.fav_spot_id === spotId || spot.spot_id === spotId)
    }

    async function getAPIStorage() {
      if (memberLevel.value < 0) return;

      loadingAPIStorage.value = true;

      const params = {
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams(params).toString();

      try {
        const response = await fetch(`${$BASEURL}/profile/getUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        setApiStorage(data);
        loadingAPIStorage.value = false;
        
        return data
      } catch (error) {
        loadingAPIStorage.value = false;
        console.warn(error);
      }
    }    

    async function updateAPIStorageItem(key, value) {
      if (memberLevel.value < 0) return;

      const params = {
        wf_token: sessionStore.token,
        name: key,
        value
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/updateUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function deleteAPIStorage() {
      if (memberLevel.value < 0) return;

      const params = {
        wf_token: sessionStore.token,
      }

      const queryString = new URLSearchParams(params).toString()

      try {
        const response = await fetch(`${$BASEURL}/profile/deleteUserStorage?${queryString}`)

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code
        }

        return data
      } catch (error) {
        console.warn(error)
      }
    }    

    async function getSpotDetailSetByProfile() {
      if (!currentProfile.value.profile_id) return [];

      const params = {
        units_wind: units.wind,
        units_temp: units.temp,
        units_distance: units.distance,
        units_precip: units.precip,
        spot_types: [1, 100, 101].join(','),
        stormprint_only: false,
        sort: favoriteSortOrder.value,
        profile_id: currentProfile.value.profile_id,
        wf_token: sessionStore.token
      }

      const queryString = new URLSearchParams( params ).toString();

      try {
        const response = await fetch(
          `${$BASEURL}/spot/getSpotDetailSetByProfile?${queryString}`
        )

        if (!response.ok) {
          throw response.status
        }

        const data = await response.json()

        if (data.status?.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }

        favoriteSpotData.value = data;

        return favoriteSpotData.value
      } catch (error) {
        console.warn(error)
      }
    }

    async function getAPIStorageItem(key) {
      if (!apiStorage.value) {
        
        if (loadingAPIStorage.value) {
          // Wait until loading is complete
          while (loadingAPIStorage.value) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } else {
          await getAPIStorage();
        }
      };
      
      return apiStorage.value ? apiStorage.value[key] : null;
    }

    // checkToken() for testing only
    async function checkToken() {
      try {
        const response = await fetch(
          `${$BASEURL}/session/checkToken?wf_token=${sessionStore.token}`
        )
  
        if (!response.ok) {
          throw response.status;
        }

        const data = await response.json();

        if (data.status.status_code !== 0 && data.status_code !== 0) {
          throw data.status || data.status_code;
        }
        
        return data;
      } catch(error) {
        console.warn(error);
      }      
    }

    async function getDefaultSettings() {
        const { data } = await apiProfile.getDefaultSettings();

      unitsDistance.value = !unitsDistance.value ? data.value.units_distance : unitsDistance.value
      unitsWind.value = !unitsWind.value ? data.value.units_speed : unitsWind.value
      unitsPrecip.value = !unitsPrecip.value ? data.value.units_precip : unitsPrecip.value
      unitsTemp.value = !unitsTemp.value ? data.value.units_temp : unitsTemp.value
      unitsHeight.value = !unitsHeight.value ? data.value.units_height : unitsHeight.value
      showNCWindToggle.value = !showNCWindToggle.value ? data.value.show_nc_wind_toggle : showNCWindToggle.value
      searchResultsDisplay.value = !searchResultsDisplay.value ? data.value.search_results_display : searchResultsDisplay.value
    }

    async function initialize() {
      if ($WEB_VIEW) {
        const calculatedHeightUnit = $SETTINGS.units_distance === 'mi' ? 'ft' : 'm'
        const calculatedPrecipUnit = $SETTINGS.units_distance === 'mi' ? 'in' : 'mm'

        unitsWind.value = $SETTINGS.units_wind
        unitsDistance.value = $SETTINGS.units_distance
        unitsPrecip.value = calculatedPrecipUnit
        unitsTemp.value = $SETTINGS.units_temp
        unitsHeight.value = $SETTINGS.units_height ? $SETTINGS.units_height : calculatedHeightUnit

        units.wind = $SETTINGS.units_wind
        units.distance = $SETTINGS.units_distance
        units.precip = calculatedPrecipUnit
        units.temp = $SETTINGS.units_temp
        units.height = $SETTINGS.units_height ? $SETTINGS.units_height : calculatedHeightUnit
        units.short_distance = calculatedHeightUnit

      } else {
        await getDefaultSettings()
      }

      initialized.value = true;
    }

    function setUser(settings) {
      accessLevel.value = settings.access_level;
      canAdsToggle.value = settings.can_ads_toggle;
      defaultAdsToggleState.value = settings.default_ads_toggle_state;
      memberLevel.value = settings.member_level;
      memberLevelName.value = settings.member_level_name;
      nonTrialMemberLevel.value = settings.non_trial_member_level;
      nonTrialMemberLevelName.value = settings.non_trial_member_level_name;
      showAds.value = settings.show_ads;
      showAdsToggle.value = settings.show_ads_toggle;
      userSignedIn.value = parseInt(settings.non_trial_member_level) === -1;
      userName.value = settings.wf_username;
    }

    function setCurrentProfile(profileId) {
      if (currentProfileId.value === profileId) {
        currentProfile.value = getProfileById(profileId);
        return currentProfile.value;
      }

      if (!profileId) {
        if (lastSelectedProfileId.value) {
          const profile = getProfileById(lastSelectedProfileId.value);

          if (profile) {
            currentProfile.value = profile;
          } else {
            setDefaultProfile();
          }
        } else {
          setDefaultProfile();
        }

        return currentProfile.value;
      }

      const foundProfile = getProfileById(profileId);

      if (!foundProfile) {
        console.warn(`Profile with ID ${profileId} not found.`);

        const currentProfileIsValid = getProfileById(currentProfileId.value);

        if (currentProfileIsValid) {
          currentProfile.value = currentProfileIsValid;

          console.warn(`Using profile ${currentProfile.value.name}. ID: ${currentProfile.value.profile_id}.`)
        } else {
          setDefaultProfile();
        }

        lastSelectedProfileId.value = currentProfileId.value;
      } else {
        currentProfile.value = foundProfile;
        lastSelectedProfileId.value = profileId;
      }

      return currentProfile.value;
    }

    function refreshCurrentProfile() {
      if (!currentProfileId.value) return null;

      const foundProfile = getProfileById(currentProfileId.value);

      if (!foundProfile) {
        console.warn(`Profile with ID ${currentProfileId.value} not found.`);

        return null;
      } else {
        currentProfile.value = foundProfile;
      }

      return currentProfile.value;
    }

    function setDefaultProfile() {
      currentProfile.value = userProfiles.value.profiles.find(profile => profile.master === 'true');

      return currentProfile.value;
    }

    function getProfileById(profileId) {
      return userProfiles.value.profiles.find(profile => parseInt(profile.profile_id) === parseInt(profileId));
    }

    function changeMasterProfileNameToMain(profiles) {
      for (let profile of profiles) {
        if (profile.master === 'true') {
          profile.name = "Main"
        }
      }

      return profiles;
    }

    function getContainerById(containerId) {
      return profileAllContainers.value.find(container => parseInt(container.container_id) === parseInt(containerId) )
    }

    function setApiStorage(data) {
      delete data.status;

      apiStorage.value = data;
    }

    function isSpotFavoritedInCurrentProfile({spotId, lat = null, lon = null}) {
      const profile = getProfileById(currentProfileId.value);
      let spotFavorited = null

      for (let favoriteSpot of profile.favorite_spots) {
        if (
          !favoriteSpot.spot_id &&
          favoriteSpot.lat === lat &&
          favoriteSpot.lon === lon
        ) {
          spotFavorited = [currentProfileId.value, favoriteSpot.fav_spot_id]
          break
        }

        if (
          favoriteSpot.spot_id &&
          spotId &&
          favoriteSpot.spot_id === spotId
        ) {
          spotFavorited = [currentProfileId.value, favoriteSpot.fav_spot_id]
          break
        }
      }

      return spotFavorited
    }

    watchEffect(() => {
      window.$EventBus.dispatchEvent('user-profile-changed', {
        profileId: currentProfile.value?.profile_id
      });
    })

    watchEffect(() => document.documentElement.dataset.showAds = showAds.value);

    watch(() => [
      unitsWind.value, 
      unitsDistance.value, 
      unitsPrecip.value, 
      unitsTemp.value, 
      unitsHeight.value
    ], async (newUnit) => {
      if ($WEB_VIEW) return;

      jweather.settings.updateUserWindSetting(newUnit[0])
      jweather.settings.updateUserDistanceSetting(newUnit[1])
      jweather.settings.updateUserPrecipSetting(newUnit[2])
      jweather.settings.updateUserTempSetting(newUnit[3])
      jweather.settings.updateUserHeightSetting(newUnit[4])

      units.wind = newUnit[0];
      units.distance = newUnit[1];
      units.precip = newUnit[2];
      units.temp = newUnit[3];
      units.height = newUnit[4];
      units.short_distance = newUnit[1] === 'mi' ? 'ft' : 'm';

      if (favoriteSpotData.value) {
        getSpotDetailSetByProfile();
      }

      updateKey.refresh();
    });

    return {
      apiStorage,
      currentProfile,
      currentProfileId,
      favoriteSpotContainers,
      favoriteSpotData,
      favoriteSortOrder,
      initialized,
      memberLevel,
      memberLevelName,
      messages,
      nonTrialMemberLevel,
      nonTrialMemberLevelName,
      profileAllContainers,
      profileContainers,
      profileMaps,
      showAds,
      showNCWindToggle,
      units,
      userName,
      userProfiles,
      userSignedIn,
      addContainerToProfile,
      addProfile,
      addSpotToProfile,
      checkToken,
      copyProfile,
      deleteAPIStorage,
      deleteContainers,
      deleteProfile,
      deleteSpotsFromProfile,
      getAPIStorage,
      getAPIStorageItem,
      getContainerById,
      getFavoriteSpotById,
      getProfileById,
      getProfiles,
      getSpotDetailSetByProfile,
      initialize,
      isSpotFavoritedInCurrentProfile,
      moveCopyItemsToProfiles,
      refreshCurrentProfile,
      removeContainerFromProfile,
      removeSpotFromProfile,
      searchResultsDisplay,
      setCurrentProfile,
      setDefaultProfile,
      setUser,
      showSettingsModal,
      sortProfiles,
      sortProfileContainers,
      sortSpotsInProfile,
      unitsDistance,
      unitsPrecip,
      unitsTemp,
      unitsWind,
      unitsHeight,
      updateAPIStorageItem,
      updateProfile,
      updateProfileContainer
    }
});
